import { nextTick, onBeforeUnmount, watch, type Ref } from 'vue';

// Nested (reference-counted) scroll lock. Each of Modal/Drawer/Workbench used
// to set `document.body.style.overflow` directly on its own open/close, so
// whichever overlay closed LAST always re-enabled scroll — even while an
// OUTER overlay (say, a Workbench) was still open underneath a confirm raised
// from inside it. A shared counter fixes it: only the 0→1 transition locks,
// only the 1→0 transition unlocks, and the original inline value is restored
// exactly once the outermost overlay is gone.
let scrollLockCount = 0;
const previousOverflowStack: string[] = [];

function lockScroll(): void {
  if (typeof document === 'undefined') return;
  if (scrollLockCount === 0) {
    previousOverflowStack.push(document.body.style.overflow);
    document.body.style.overflow = 'hidden';
  }
  scrollLockCount += 1;
}

function unlockScroll(): void {
  if (typeof document === 'undefined') return;
  if (scrollLockCount === 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount === 0) {
    document.body.style.overflow = previousOverflowStack.pop() ?? '';
  }
}

/** Reference-counted scroll lock: call once per overlay primitive instance, keyed to its own `open`. */
export function useScrollLock(open: Ref<boolean>): void {
  let locked = false;
  watch(
    open,
    (isOpen) => {
      if (isOpen && !locked) {
        lockScroll();
        locked = true;
      } else if (!isOpen && locked) {
        unlockScroll();
        locked = false;
      }
    },
    { immediate: true },
  );
  onBeforeUnmount(() => {
    if (locked) {
      unlockScroll();
      locked = false;
    }
  });
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusablesIn(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

// Shared stack of currently-open overlay instances (Modal/Drawer/Workbench/
// CommandPalette), topmost last. A nested overlay (a NoticeDialog confirm
// raised from inside a Modal, say) must be the ONLY one that Tab cycles
// inside and the ONLY one Escape closes — otherwise two independent traps
// fight over every Tab keypress, and one Escape press closes both dialogs at
// once. Every `useFocusTrap` instance registers itself here on open and pops
// itself on close/unmount; callers that also need Escape gating (the
// primitives' own `onKey` handlers) read `isTopmost()` from the returned
// controller instead of keeping their own notion of "on top". Those handlers
// also skip an Escape whose `defaultPrevented` is set: an inner control that
// consumed the key (a Combobox closing its list) says so via preventDefault(),
// and the overlay around it must then stay open (#943).
interface TrapEntry {
  id: number;
  containerRef: Ref<HTMLElement | null>;
}
let trapIdSeq = 0;
const trapStack: TrapEntry[] = [];
let trapListenerInstalled = false;

function topTrap(): TrapEntry | undefined {
  return trapStack[trapStack.length - 1];
}

function onGlobalTrapKeydown(e: KeyboardEvent): void {
  if (e.key !== 'Tab') return;
  const top = topTrap();
  if (!top) return;
  const items = focusablesIn(top.containerRef.value);
  if (items.length === 0) {
    e.preventDefault();
    top.containerRef.value?.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement as HTMLElement | null;
  if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  } else if (!active || !items.includes(active)) {
    // Focus drifted outside the trap (e.g. a programmatic focus() elsewhere) — pull it back in.
    e.preventDefault();
    first.focus();
  }
}

export interface FocusTrapController {
  /** True while this instance is the topmost open overlay — gates Escape-to-close. */
  isTopmost: () => boolean;
}

/**
 * Focus trap + focus return for a teleported overlay.
 *
 * `containerRef` must resolve to the overlay's root (dialog) element by the
 * time `open` flips true — it is read after a `nextTick` so the v-if branch
 * has mounted. While open, Tab/Shift+Tab cycle within the container instead
 * of escaping to the page underneath (which the teleport target, `<body>`,
 * would otherwise make trivial). On close, focus returns to whatever element
 * had it before the overlay opened, so a keyboard or screen-reader user does
 * not resurface at the top of `<body>`.
 *
 * Nested-safe: only the topmost open instance's container is ever cycled —
 * a single shared listener acts on the top of `trapStack`, rather than each
 * instance running its own independent listener and fighting the others.
 */
export function useFocusTrap(open: Ref<boolean>, containerRef: Ref<HTMLElement | null>): FocusTrapController {
  let returnTarget: HTMLElement | null = null;
  let entryId: number | null = null;

  function isTopmost(): boolean {
    return entryId !== null && topTrap()?.id === entryId;
  }

  function popSelf(): void {
    if (entryId === null) return;
    const idx = trapStack.findIndex((t) => t.id === entryId);
    if (idx !== -1) trapStack.splice(idx, 1);
    entryId = null;
    if (trapStack.length === 0 && trapListenerInstalled && typeof document !== 'undefined') {
      document.removeEventListener('keydown', onGlobalTrapKeydown, true);
      trapListenerInstalled = false;
    }
  }

  watch(
    open,
    async (isOpen) => {
      if (typeof document === 'undefined') return;
      if (isOpen) {
        returnTarget = document.activeElement as HTMLElement | null;
        entryId = ++trapIdSeq;
        trapStack.push({ id: entryId, containerRef });
        if (!trapListenerInstalled) {
          document.addEventListener('keydown', onGlobalTrapKeydown, true);
          trapListenerInstalled = true;
        }
        await nextTick();
        // Respect focus a call site already placed deliberately (e.g. a form's
        // own first-field focus) instead of always stealing it to the first
        // focusable element — only force focus in if nothing inside the
        // dialog already has it.
        const active = document.activeElement as HTMLElement | null;
        if (!active || !containerRef.value?.contains(active)) {
          const items = focusablesIn(containerRef.value);
          (items[0] ?? containerRef.value)?.focus();
        }
      } else {
        popSelf();
        if (returnTarget && document.contains(returnTarget)) {
          returnTarget.focus();
        }
        returnTarget = null;
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    if (typeof document === 'undefined') return;
    popSelf();
  });

  return { isTopmost };
}
